import * as types from 'src/state/ducks/notification/types';
import { Actions } from 'src/state/ducks/notification/actions';
import { BakedBreadEntity, bakedBreadInitialEntity } from 'src/firebase/db/BakedBreads';
import { OrderBreadEntity, orderBreadInitialEntity } from 'src/firebase/db/OrderBreads';
import {
  NotificationEntity,
  NotificationProps,
  notificationInitialEntity,
} from 'src/firebase/db/Notifications';
import { ValidationResponse } from 'src/firebase/db/ValidationManager';
import { APIFailedResponse } from 'src/firebase/db/DatabaseAdapter';

export interface Props {
  isFetching: boolean;
  isValidated: boolean;
  isNotified: boolean;
  resource: NotificationEntity[];
  draft: NotificationEntity;
  validationErrors: ValidationResponse['errors'];
  apiError: APIFailedResponse['error'] | undefined;
  orderBreads: {
    isFetching: boolean;
    isDeleted: boolean;
    resource: NotificationEntity[];
    apiError: APIFailedResponse['error'] | undefined;
  };
  filterByDate: {
    isFetching: boolean;
    resource: NotificationProps[];
    apiError: APIFailedResponse['error'] | undefined;
  };
  templates: {
    isFetching: boolean;
    resource: NotificationEntity[];
    apiError: APIFailedResponse['error'] | undefined;
  };
  reservations: {
    draft: {
      bakedBread: BakedBreadEntity;
      orderBread: OrderBreadEntity;
      notification: NotificationEntity;
    };
    isFetching: boolean;
    isValidated: boolean;
    isDeleted: boolean;
    resource: NotificationEntity[];
    apiError: APIFailedResponse['error'] | undefined;
    validationErrors: ValidationResponse['errors'];
  };
  reservation: {
    isFetching: boolean;
    resource: NotificationProps;
    apiError: APIFailedResponse['error'] | undefined;
  };
}

const initialState: Props = {
  isFetching: false,
  isValidated: false,
  isNotified: false,
  resource: [],
  draft: notificationInitialEntity,
  validationErrors: {},
  apiError: undefined,
  orderBreads: {
    isFetching: false,
    isDeleted: false,
    resource: [],
    apiError: undefined,
  },
  filterByDate: {
    isFetching: false,
    resource: [],
    apiError: undefined,
  },
  templates: {
    isFetching: false,
    resource: [],
    apiError: undefined,
  },
  reservations: {
    draft: {
      bakedBread: bakedBreadInitialEntity,
      orderBread: orderBreadInitialEntity,
      notification: notificationInitialEntity,
    },
    isValidated: false,
    isDeleted: false,
    isFetching: false,
    resource: [],
    apiError: undefined,
    validationErrors: {},
  },
  reservation: {
    isFetching: false,
    resource: {
      ...notificationInitialEntity,
      order_breads: [],
    },
    apiError: undefined,
  },
};

const notificationReducer = (state = initialState, action: Actions): Props => {
  switch (action.type) {
    case types.GET_NOTIFICATIONS: {
      return {
        ...state,
        isFetching: true,
      };
    }

    case types.SUCCESS_GET_NOTIFICATIONS: {
      return {
        ...state,
        resource: action.payload.data,
        isFetching: false,
      };
    }

    case types.FAIL_GET_NOTIFICATIONS: {
      return {
        ...state,
        isFetching: false,
        validationErrors: 'errors' in action.payload ? action.payload.errors : {},
        apiError: 'error' in action.payload ? action.payload.error : undefined,
      };
    }

    case types.HAS_NOTIFICATIONS: {
      return {
        ...state,
        isFetching: true,
        isNotified: false,
      };
    }

    case types.SUCCESS_HAS_NOTIFICATIONS: {
      return {
        ...state,
        isNotified: action.payload.data,
        isFetching: false,
      };
    }

    case types.FAIL_HAS_NOTIFICATIONS: {
      return {
        ...state,
        isFetching: false,
        isNotified: false,
        apiError: 'error' in action.payload ? action.payload.error : undefined,
      };
    }

    case types.GET_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          draft: {
            bakedBread: bakedBreadInitialEntity,
            orderBread: orderBreadInitialEntity,
            notification: notificationInitialEntity,
          },
          isFetching: true,
        },
      };
    }

    case types.SUCCESS_GET_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          draft: action.payload.data,
          isFetching: false,
          apiError: undefined,
        },
      };
    }

    case types.FAIL_GET_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: false,
          resource: [],
          apiError: 'error' in action.payload ? action.payload.error : undefined,
        },
      };
    }

    case types.GET_RESERVATIONS: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: true,
        },
      };
    }

    case types.SUCCESS_GET_RESERVATIONS: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          resource: action.payload.data,
          isFetching: false,
          apiError: undefined,
        },
      };
    }

    case types.FAIL_GET_RESERVATIONS: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: false,
          resource: [],
          apiError: 'error' in action.payload ? action.payload.error : undefined,
        },
      };
    }

    case types.GET_DAY_RESERVATION: {
      return {
        ...state,
        reservation: {
          resource: {
            ...notificationInitialEntity,
            order_breads: [],
          },
          isFetching: true,
          apiError: undefined,
        },
      };
    }

    case types.SUCCESS_GET_DAY_RESERVATION: {
      return {
        ...state,
        reservation: {
          resource: action.payload.data,
          isFetching: false,
          apiError: undefined,
        },
      };
    }

    case types.FAIL_GET_DAY_RESERVATION: {
      return {
        ...state,
        reservation: {
          ...state.reservation,
          isFetching: false,
          apiError: 'error' in action.payload ? action.payload.error : undefined,
        },
      };
    }

    case types.INIT_NOTIFICATION: {
      return initialState;
    }

    case types.EDIT_NOTIFICATION: {
      return {
        ...state,
        isValidated: false,
        validationErrors: {},
      };
    }

    case types.INIT_RESERVATION: {
      return {
        ...state,
        reservations: {
          draft: {
            bakedBread: bakedBreadInitialEntity,
            orderBread: orderBreadInitialEntity,
            notification: notificationInitialEntity,
          },
          isFetching: false,
          isDeleted: false,
          isValidated: false,
          resource: [],
          apiError: undefined,
          validationErrors: {},
        },
      };
    }

    case types.EDIT_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isValidated: false,
          validationErrors: {},
        },
      };
    }

    case types.CANCEL_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: true,
        },
      };
    }

    case types.SUCCESS_CANCEL_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: false,
          apiError: undefined,
        },
      };
    }

    case types.FAIL_CANCEL_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: false,
          apiError: 'error' in action.payload ? action.payload.error : undefined,
        },
      };
    }

    case types.VALIDATE_NOTIFICATION: {
      return {
        ...state,
        isValidated: false,
        draft: action.payload,
      };
    }

    case types.SUCCESS_VALIDATE_NOTIFICATION: {
      return {
        ...state,
        isValidated: true,
      };
    }

    case types.FAIL_VALIDATE_NOTIFICATION: {
      return {
        ...state,
        isValidated: false,
        validationErrors: 'errors' in action.payload ? action.payload.errors : {},
        apiError: 'error' in action.payload ? action.payload.error : undefined,
      };
    }

    case types.CREATE_NOTIFICATION: {
      return {
        ...state,
        isFetching: true,
      };
    }

    case types.SUCCESS_CREATE_NOTIFICATION: {
      return {
        ...state,
        isFetching: false,
      };
    }

    case types.FAIL_CREATE_NOTIFICATION: {
      return {
        ...state,
        isFetching: false,
        validationErrors: 'errors' in action.payload ? action.payload.errors : {},
        apiError: 'error' in action.payload ? action.payload.error : undefined,
      };
    }

    case types.CREATE_NOTIFICATIONS: {
      return {
        ...state,
        isFetching: true,
      };
    }

    case types.SUCCESS_CREATE_NOTIFICATIONS: {
      return {
        ...state,
        isFetching: false,
      };
    }

    case types.FAIL_CREATE_NOTIFICATIONS: {
      return {
        ...state,
        isFetching: false,
        validationErrors: 'errors' in action.payload ? action.payload.errors : {},
        apiError: 'error' in action.payload ? action.payload.error : undefined,
      };
    }

    case types.CREATE_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: true,
        },
      };
    }

    case types.SUCCESS_CREATE_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: false,
        },
      };
    }

    case types.FAIL_CREATE_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: false,
          apiError: 'error' in action.payload ? action.payload.error : undefined,
          validationErrors: 'errors' in action.payload ? action.payload.errors : {},
        },
      };
    }

    case types.UPDATE_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: true,
        },
      };
    }

    case types.SUCCESS_UPDATE_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: false,
        },
      };
    }

    case types.FAIL_UPDATE_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: false,
          apiError: 'error' in action.payload ? action.payload.error : undefined,
          validationErrors: 'errors' in action.payload ? action.payload.errors : {},
        },
      };
    }

    case types.DELETE_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: true,
          isDeleted: false,
        },
      };
    }

    case types.SUCCESS_DELETE_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: false,
          isDeleted: true,
        },
      };
    }

    case types.FAIL_DELETE_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: false,
          isDeleted: false,
        },
      };
    }

    case types.DUPLICATE_RESERVATIONS: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: true,
          apiError: undefined,
        },
      };
    }

    case types.SUCCESS_DUPLICATE_RESERVATIONS: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: false,
        },
      };
    }

    case types.FAIL_DUPLICATE_RESERVATIONS: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isFetching: false,
          apiError: 'error' in action.payload ? action.payload.error : undefined,
        },
      };
    }

    case types.VALIDATE_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          draft: action.payload,
          isValidated: false,
          validationErrors: {},
          apiError: undefined,
        },
      };
    }

    case types.SUCCESS_VALIDATE_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isValidated: true,
        },
      };
    }

    case types.FAIL_VALIDATE_RESERVATION: {
      return {
        ...state,
        reservations: {
          ...state.reservations,
          isValidated: false,
          validationErrors: 'errors' in action.payload ? action.payload.errors : {},
          apiError: 'error' in action.payload ? action.payload.error : undefined,
        },
      };
    }

    case types.REMOVE_ORDER_BREAD_FROM_NOTIFICATION: {
      return {
        ...state,
        orderBreads: {
          ...state.orderBreads,
          isFetching: true,
          isDeleted: false,
          apiError: undefined,
        },
      };
    }

    case types.SUCCESS_REMOVE_ORDER_BREAD_FROM_NOTIFICATION: {
      return {
        ...state,
        orderBreads: {
          ...state.orderBreads,
          isFetching: false,
          isDeleted: true,
        },
      };
    }

    case types.FAIL_REMOVE_ORDER_BREAD_FROM_NOTIFICATION: {
      console.log(action.payload);
      return {
        ...state,
        orderBreads: {
          ...state.orderBreads,
          isFetching: false,
          isDeleted: false,
          apiError: 'error' in action.payload ? action.payload.error : undefined,
        },
      };
    }

    case types.COMPLETE_REMOVE_ORDER_BREAD_FROM_NOTIFICATION: {
      return {
        ...state,
        orderBreads: {
          ...state.orderBreads,
          isFetching: false,
          isDeleted: false,
          apiError: undefined,
        },
      };
    }

    case types.GET_DAY_NOTIFICATIONS: {
      return {
        ...state,
        filterByDate: {
          ...state.filterByDate,
          isFetching: true,
        },
      };
    }

    case types.SUCCESS_GET_DAY_NOTIFICATIONS: {
      return {
        ...state,
        filterByDate: {
          ...state.filterByDate,
          resource: action.payload.data,
          isFetching: false,
          apiError: undefined,
        },
      };
    }

    case types.FAIL_GET_DAY_NOTIFICATIONS: {
      return {
        ...state,
        filterByDate: {
          ...state.filterByDate,
          isFetching: false,
          resource: [],
          apiError: 'error' in action.payload ? action.payload.error : undefined,
        },
      };
    }

    case types.GET_TEMPLATES_NOTIFICATIONS: {
      return {
        ...state,
        templates: {
          ...state.templates,
          isFetching: true,
        },
      };
    }

    case types.SUCCESS_GET_TEMPLATES_NOTIFICATIONS: {
      return {
        ...state,
        templates: {
          ...state.templates,
          resource: action.payload.data,
          isFetching: false,
          apiError: undefined,
        },
      };
    }

    case types.FAIL_GET_TEMPLATES_NOTIFICATIONS: {
      return {
        ...state,
        templates: {
          ...state.templates,
          isFetching: false,
          resource: [],
          apiError: 'error' in action.payload ? action.payload.error : undefined,
        },
      };
    }

    default:
      return state;
  }
};

export default notificationReducer;
